import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { toast } from 'react-hot-toast';
import {
  RelatedRecord,
  RelationshipForm,
  MediaRelationship,
  RelationshipNode,
  APIResponse,
  UseRelatedRecordsResult,
} from '../types/mediaRecord';

// API service functions
const relatedRecordsApi = {
  get: async (
    mediaId: string,
    tenantId: string,
    params: {
      maxDepth?: number;
      includeReverse?: boolean;
      limit?: number;
    } = {}
  ): Promise<RelatedRecord[]> => {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, String(value));
      }
    });

    const response = await fetch(`/api/media/${mediaId}/related?${searchParams}`, {
      headers: {
        'X-Tenant-ID': tenantId,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch related records: ${response.statusText}`);
    }

    const result: APIResponse<RelatedRecord[]> = await response.json();
    
    if (!result.success || !result.data) {
      throw new Error(result.message || 'Failed to fetch related records');
    }

    return result.data;
  },

  create: async (
    mediaId: string,
    tenantId: string,
    relationshipData: RelationshipForm
  ): Promise<MediaRelationship> => {
    const response = await fetch(`/api/media/${mediaId}/related`, {
      method: 'POST',
      headers: {
        'X-Tenant-ID': tenantId,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(relationshipData),
    });

    if (!response.ok) {
      throw new Error(`Failed to create relationship: ${response.statusText}`);
    }

    const result: APIResponse<MediaRelationship> = await response.json();
    
    if (!result.success || !result.data) {
      throw new Error(result.message || 'Failed to create relationship');
    }

    return result.data;
  },

  delete: async (
    mediaId: string,
    tenantId: string,
    relationshipId: string
  ): Promise<void> => {
    const response = await fetch(`/api/media/${mediaId}/related/${relationshipId}`, {
      method: 'DELETE',
      headers: {
        'X-Tenant-ID': tenantId,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to delete relationship: ${response.statusText}`);
    }

    const result: APIResponse = await response.json();
    
    if (!result.success) {
      throw new Error(result.message || 'Failed to delete relationship');
    }
  },
};

// Helper function to get tenant ID
const getTenantId = (): string => {
  return localStorage.getItem('tenantId') || process.env.NEXT_PUBLIC_DEFAULT_TENANT_ID || '';
};

// Helper function to build tree structure from flat related records
const buildRelationshipTree = (records: RelatedRecord[], rootId: string): RelationshipNode[] => {
  const nodeMap = new Map<string, RelationshipNode>();
  const tree: RelationshipNode[] = [];

  // Create nodes for all records
  records.forEach(record => {
    if (!nodeMap.has(record.media_id)) {
      nodeMap.set(record.media_id, {
        id: record.media_id,
        title: record.title,
        media_type: record.media_type,
        format: record.format,
        children: [],
        relationships: [],
      });
    }

    const node = nodeMap.get(record.media_id)!;
    node.relationships.push({
      type: record.relationship.type,
      subtype: record.relationship.subtype,
      strength: record.relationship.strength,
      depth: record.relationship.depth,
    });
  });

  // Build tree structure based on depth and relationships
  const depthGroups = new Map<number, RelatedRecord[]>();
  records.forEach(record => {
    const depth = record.relationship.depth;
    if (!depthGroups.has(depth)) {
      depthGroups.set(depth, []);
    }
    depthGroups.get(depth)!.push(record);
  });

  // Sort by depth and add to tree
  const sortedDepths = Array.from(depthGroups.keys()).sort((a, b) => a - b);
  
  sortedDepths.forEach(depth => {
    const recordsAtDepth = depthGroups.get(depth)!;
    
    recordsAtDepth.forEach(record => {
      const node = nodeMap.get(record.media_id)!;
      
      if (depth === 1) {
        // Direct relationships go to root level
        tree.push(node);
      } else {
        // Find parent and add as child
        const parentPath = record.relationship.path.slice(0, -1);
        const parentId = parentPath[parentPath.length - 1];
        const parentNode = nodeMap.get(parentId);
        
        if (parentNode && !parentNode.children.find(child => child.id === node.id)) {
          parentNode.children.push(node);
        }
      }
    });
  });

  return tree;
};

/**
 * Custom hook for managing related records
 */
export const useRelatedRecords = (
  mediaId: string,
  options: {
    maxDepth?: number;
    includeReverse?: boolean;
    limit?: number;
    autoRefresh?: boolean;
  } = {}
): UseRelatedRecordsResult => {
  const queryClient = useQueryClient();
  const tenantId = getTenantId();

  const {
    maxDepth = 3,
    includeReverse = true,
    limit = 100,
    autoRefresh = false,
  } = options;

  // Query for related records
  const {
    data = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['relatedRecords', mediaId, tenantId, maxDepth, includeReverse, limit],
    queryFn: () => relatedRecordsApi.get(mediaId, tenantId, {
      maxDepth,
      includeReverse,
      limit,
    }),
    enabled: !!mediaId && !!tenantId,
    staleTime: 60000, // 1 minute
    gcTime: 300000, // 5 minutes
    refetchInterval: autoRefresh ? 30000 : false,
    retry: (failureCount, error) => {
      if (error.message.includes('404') || error.message.includes('401')) {
        return false;
      }
      return failureCount < 3;
    },
  });

  // Build tree structure
  const tree = useMemo(() => {
    return buildRelationshipTree(data, mediaId);
  }, [data, mediaId]);

  // Mutation for adding relationship
  const addRelationshipMutation = useMutation({
    mutationFn: (relationshipData: RelationshipForm) =>
      relatedRecordsApi.create(mediaId, tenantId, relationshipData),
    onMutate: async (newRelationship) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ 
        queryKey: ['relatedRecords', mediaId, tenantId] 
      });

      // Snapshot the previous value
      const previousRecords = queryClient.getQueryData<RelatedRecord[]>([
        'relatedRecords', mediaId, tenantId, maxDepth, includeReverse, limit
      ]);

      // Optimistically update (simplified - just invalidate to refetch)
      // In a real implementation, you might create a placeholder record

      return { previousRecords };
    },
    onError: (error, newRelationship, context) => {
      // Rollback on error
      if (context?.previousRecords) {
        queryClient.setQueryData([
          'relatedRecords', mediaId, tenantId, maxDepth, includeReverse, limit
        ], context.previousRecords);
      }
      
      toast.error(`Failed to create relationship: ${error.message}`);
    },
    onSuccess: (relationship) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['relatedRecords', mediaId] });
      queryClient.invalidateQueries({ queryKey: ['relatedRecords', relationship.target_media_id] });
      
      toast.success('Relationship created successfully');
    },
  });

  // Mutation for removing relationship
  const removeRelationshipMutation = useMutation({
    mutationFn: (relationshipId: string) =>
      relatedRecordsApi.delete(mediaId, tenantId, relationshipId),
    onMutate: async (relationshipId) => {
      await queryClient.cancelQueries({ 
        queryKey: ['relatedRecords', mediaId, tenantId] 
      });

      const previousRecords = queryClient.getQueryData<RelatedRecord[]>([
        'relatedRecords', mediaId, tenantId, maxDepth, includeReverse, limit
      ]);

      // Optimistically remove the relationship
      if (previousRecords) {
        const updatedRecords = previousRecords.filter(
          record => !record.relationship.path.includes(relationshipId)
        );
        
        queryClient.setQueryData([
          'relatedRecords', mediaId, tenantId, maxDepth, includeReverse, limit
        ], updatedRecords);
      }

      return { previousRecords };
    },
    onError: (error, relationshipId, context) => {
      // Rollback on error
      if (context?.previousRecords) {
        queryClient.setQueryData([
          'relatedRecords', mediaId, tenantId, maxDepth, includeReverse, limit
        ], context.previousRecords);
      }
      
      toast.error(`Failed to delete relationship: ${error.message}`);
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['relatedRecords', mediaId] });
      
      toast.success('Relationship deleted successfully');
    },
  });

  const addRelationship = async (relationshipData: RelationshipForm): Promise<void> => {
    addRelationshipMutation.mutateAsync(relationshipData);
  };

  const removeRelationship = async (relationshipId: string): Promise<void> => {
    return removeRelationshipMutation.mutateAsync(relationshipId);
  };

  return {
    data,
    isLoading,
    error: error as Error | null,
    addRelationship,
    removeRelationship,
    tree,
  };
};

/**
 * Hook for searching and managing relationships across the platform
 */
export const useRelationshipSearch = () => {
  const tenantId = getTenantId();

  const searchMutation = useMutation({
    mutationFn: async (searchParams: {
      keyword?: string;
      relationshipType?: string;
      mediaId?: string;
      limit?: number;
    }) => {
      const params = new URLSearchParams();
      
      Object.entries(searchParams).forEach(([key, value]) => {
        if (value !== undefined) {
          params.append(key, String(value));
        }
      });

      const response = await fetch(`/api/relationships/search?${params}`, {
        headers: {
          'X-Tenant-ID': tenantId,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to search relationships: ${response.statusText}`);
      }

      const result: APIResponse<MediaRelationship[]> = await response.json();
      
      if (!result.success || !result.data) {
        throw new Error(result.message || 'Failed to search relationships');
      }

      return result.data;
    },
    onError: (error) => {
      toast.error(`Search failed: ${error.message}`);
    },
  });

  return {
    search: searchMutation.mutate,
    searchAsync: searchMutation.mutateAsync,
    isSearching: searchMutation.isPending,
    searchResults: searchMutation.data,
    searchError: searchMutation.error,
  };
};

/**
 * Hook for bulk relationship operations
 */
export const useBulkRelationshipOperations = () => {
  const queryClient = useQueryClient();
  const tenantId = getTenantId();

  const bulkCreateMutation = useMutation({
    mutationFn: async (relationships: Array<{
      sourceMediaId: string;
      relationshipData: RelationshipForm;
    }>) => {
      const results = await Promise.allSettled(
        relationships.map(({ sourceMediaId, relationshipData }) =>
          relatedRecordsApi.create(sourceMediaId, tenantId, relationshipData)
        )
      );

      const successful = results.filter(result => result.status === 'fulfilled').length;
      const failed = results.filter(result => result.status === 'rejected').length;

      return { successful, failed, total: relationships.length };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['relatedRecords'] });
      
      toast.success(
        `Bulk operation completed: ${result.successful} successful, ${result.failed} failed`
      );
    },
    onError: (error) => {
      toast.error(`Bulk operation failed: ${error.message}`);
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (relationships: Array<{
      mediaId: string;
      relationshipId: string;
    }>) => {
      const results = await Promise.allSettled(
        relationships.map(({ mediaId, relationshipId }) =>
          relatedRecordsApi.delete(mediaId, tenantId, relationshipId)
        )
      );

      const successful = results.filter(result => result.status === 'fulfilled').length;
      const failed = results.filter(result => result.status === 'rejected').length;

      return { successful, failed, total: relationships.length };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['relatedRecords'] });
      
      toast.success(
        `Bulk deletion completed: ${result.successful} successful, ${result.failed} failed`
      );
    },
    onError: (error) => {
      toast.error(`Bulk deletion failed: ${error.message}`);
    },
  });

  return {
    bulkCreate: bulkCreateMutation.mutate,
    bulkDelete: bulkDeleteMutation.mutate,
    isBulkCreating: bulkCreateMutation.isPending,
    isBulkDeleting: bulkDeleteMutation.isPending,
  };
};

/**
 * Hook for relationship recommendations
 */
export const useRelationshipRecommendations = (mediaId: string) => {
  const tenantId = getTenantId();

  const { data: recommendations, isLoading } = useQuery({
    queryKey: ['relationshipRecommendations', mediaId, tenantId],
    queryFn: async () => {
      const response = await fetch(`/api/media/${mediaId}/relationship-recommendations`, {
        headers: {
          'X-Tenant-ID': tenantId,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch recommendations: ${response.statusText}`);
      }

      const result: APIResponse<RelatedRecord[]> = await response.json();
      
      if (!result.success || !result.data) {
        throw new Error(result.message || 'Failed to fetch recommendations');
      }

      return result.data;
    },
    enabled: !!mediaId && !!tenantId,
    staleTime: 300000, // 5 minutes
    gcTime: 600000, // 10 minutes
  });

  return {
    recommendations: recommendations || [],
    isLoading,
  };
};

/**
 * Hook for prefetching related records
 */
export const usePrefetchRelatedRecords = () => {
  const queryClient = useQueryClient();
  const tenantId = getTenantId();

  const prefetchRelatedRecords = (
    mediaId: string,
    maxDepth: number = 3,
    limit: number = 100
  ) => {
    queryClient.prefetchQuery({
      queryKey: ['relatedRecords', mediaId, tenantId, maxDepth, true, limit],
      queryFn: () => relatedRecordsApi.get(mediaId, tenantId, {
        maxDepth,
        includeReverse: true,
        limit,
      }),
      staleTime: 60000,
    });
  };

  return { prefetchRelatedRecords };
};